/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Text button + MUI Menu for labeled option lists (Figma UXD-613/614).
 *
 * @file LabeledOptionsMenu.jsx
 * @author Gladson Samuel S
 * Each option must be `{ label, value }`. The `value` prop is the selected option’s `value`.
 *
 * @example
 * <LabeledOptionsMenu
 *   options={[{ value: 'std', label: 'Standard' }]}
 *   value={selectedValue}
 *   title={selectedOption ? `Due dates: ${selectedOption.label}` : null}
 *   onSelect={setSelectedOption}
 *   classes={{
 *     triggerButton: parentClasses.customTrigger
 *   }}
 * />
 */

import React from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Box from '@material-ui/core/Box';
import Icon from '../icons/Icon';
import useLabeledOptionsMenuStyles from './LabeledOptionsMenuStyles';

const MENU_ID = 'labeled-options-menu';

const LabeledOptionsMenu = (props) => {
  const classes = useLabeledOptionsMenuStyles(props);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const {
    options = [],
    value,
    onSelect,
    buttonLabel,
    startIconName,
    endIconName,
    selectedCheckIconName,
    ButtonProps = {},
    MenuProps = {}
  } = props;

  const renderButtonLabel = () => {
    if (buttonLabel === undefined || buttonLabel === null) {
      return null;
    }

    return buttonLabel;
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (option) => {
    if (option?.value === value) {
      return;
    }

    onSelect(option);
    handleClose();
  };

  const {
    onClick: buttonOnClick,
    startIcon: buttonStartIcon,
    endIcon: buttonEndIcon,
    ...restButtonProps
  } = ButtonProps;

  const {
    onClose: menuOnClose,
    MenuListProps: menuListPropsUser,
    ...restMenuProps
  } = MenuProps;

  const renderButton = () => (
    <Button
      {...restButtonProps}
      variant="text"
      disableRipple
      aria-haspopup="true"
      aria-expanded={open ? 'true' : undefined}
      aria-controls={open ? MENU_ID : undefined}
      id={`${MENU_ID}-trigger`}
      classes={{
        label: classes.triggerButtonLabel,
        root: classes.triggerButton,
        startIcon: classes.startIconContainer,
        endIcon: classes.endIconContainer
      }}
      startIcon={buttonStartIcon || (
        <Icon
          name={startIconName}
          className={classes.startIcon}
        />
      )}
      endIcon={buttonEndIcon || (
        <Icon
          name={endIconName}
          className={classes.endIcon}
        />
      )}
      onClick={(e) => {
        if (buttonOnClick) {
          buttonOnClick(e);
        }

        handleOpen(e);
      }}
    >
      {renderButtonLabel()}
    </Button>
  );

  const renderMenuList = (option, index) => {
    if (!option) {
      return null;
    }

    const selected = option.value === value;
    const itemKey = option.value != null ? String(option.value) : `opt-${index}`;
    const itemClassName = selected
      ? `${classes.menuItem} ${classes.menuItemSelected}`
      : classes.menuItem;

    return (
      <MenuItem
        key={itemKey}
        disableRipple
        selected={selected}
        classes={{ root: itemClassName }}
        onClick={() => handleSelect(option)}
      >
        <span className={classes.menuItemLabel}>{option.label}</span>
        {selected ? (
          <Icon
            name={selectedCheckIconName}
            className={classes.checkIcon}
          />
        ) : (
          <span className={classes.checkIcon} aria-hidden style={{ visibility: 'hidden' }} />
        )}
      </MenuItem>
    );
  };

  const renderMenu = () => {
    if (!options || options.length === 0) {
      return null;
    }

    return (
      <Menu
        {...restMenuProps}
        id={MENU_ID}
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={(e, reason) => {
          if (menuOnClose) {
            menuOnClose(e, reason);
          }

          handleClose();
        }}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        classes={{
          paper: classes.menuPaper,
          list: classes.menuList
        }}
        MenuListProps={{
          disablePadding: true,
          ...menuListPropsUser
        }}
      >
        {options.map((option, index) => renderMenuList(option, index))}
      </Menu>
    );
  };

  return (
    <Box className={classes.root}>
      {renderButton()}
      {renderMenu()}
    </Box>
  );
};

const optionShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
  label: PropTypes.node.isRequired
});

LabeledOptionsMenu.propTypes = {
  options: PropTypes.arrayOf(optionShape),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool
  ]),
  onSelect: PropTypes.func.isRequired,
  buttonLabel: PropTypes.node,
  startIconName: PropTypes.string,
  endIconName: PropTypes.string,
  selectedCheckIconName: PropTypes.string,
  classes: PropTypes.object,
  ButtonProps: PropTypes.object,
  MenuProps: PropTypes.object
};

LabeledOptionsMenu.defaultProps = {
  options: [],
  value: undefined,
  buttonLabel: undefined,
  startIconName: 'CalendarIcon',
  endIconName: 'CaretUpIcon',
  selectedCheckIconName: 'TickIcon',
  classes: undefined,
  ButtonProps: {},
  MenuProps: {}
};

export default LabeledOptionsMenu;
